import {
  flip,
  offset,
  Strategy,
  useDismiss,
  useFloating,
  UseFloatingReturn,
  useInteractions,
  UseInteractionsReturn,
} from '@floating-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import React, {
  ComponentPropsWithoutRef,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

interface AvoSelectContextType<T = any> {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setReference: UseFloatingReturn['refs']['setReference'];
  setFloating: UseFloatingReturn['refs']['setFloating'];
  getReferenceProps: UseInteractionsReturn['getReferenceProps'];
  getFloatingProps: UseInteractionsReturn['getFloatingProps'];
  reference: UseFloatingReturn['elements']['reference'];
  floating: UseFloatingReturn['elements']['floating'];
  x: number;
  y: number;
  strategy: Strategy;
  selectedItem: T;
  setSelectedItem: React.Dispatch<React.SetStateAction<T>>;
  triggerRef: React.MutableRefObject<HTMLDivElement | null>;
}

const defaultScrollContainerElement = 'div';
type AvoSelectProps<T, E extends React.ElementType = typeof defaultScrollContainerElement> = Omit<
  ComponentPropsWithoutRef<E>,
  'onChange'
> & {
  as?: E;
  value: T;
  onChange: (value: T) => void;
};

interface AvoSelectTriggerProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode | ((props: { isOpen: boolean }) => React.ReactNode);
}

interface AvoSelectItemboxProps extends React.HTMLAttributes<HTMLDivElement> {}

interface AvoSelectItemProps<T> extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode | ((props: { isSelected: boolean }) => React.ReactNode);
  value: T;
}

const AvoSelectContext = createContext<AvoSelectContextType | undefined>(undefined);

export const AvoSelect = <T,>({ as, onChange, value, ...props }: AvoSelectProps<T>) => {
  const Component = as ?? defaultScrollContainerElement;

  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const {
    x,
    y,
    refs: { setReference, setFloating },
    elements: { reference, floating },
    strategy,
    context,
  } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    middleware: [offset(5), flip()],
    // whileElementsMounted: autoUpdate,
  });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  return (
    <AvoSelectContext.Provider
      value={{
        isOpen,
        setIsOpen,
        setReference,
        setFloating,
        getReferenceProps,
        getFloatingProps,
        reference,
        floating,
        x,
        y,
        strategy,
        selectedItem: value,
        setSelectedItem: onChange,
        triggerRef,
      }}
    >
      <Component {...props} />
    </AvoSelectContext.Provider>
  );
};

export const AvoSelectTrigger = ({ children, className, ...props }: AvoSelectTriggerProps) => {
  const context = useContext(AvoSelectContext);
  if (!context) {
    throw new Error('Trigger must be used within a AvoSelectContext');
  }
  const { setReference, setIsOpen, isOpen, triggerRef, getReferenceProps } = context;
  return (
    <div
      className={`cursor-pointer ${className}`}
      ref={(node) => {
        setReference(node);
        triggerRef.current = node;
      }}
      {...getReferenceProps({ ...props, onClick: () => setIsOpen((prev) => !prev) })}
    >
      {typeof children === 'function' ? children({ isOpen }) : children}
    </div>
  );
};

export const AvoSelectItembox = ({ children, ...props }: AvoSelectItemboxProps) => {
  const context = useContext(AvoSelectContext);
  const [width, setWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (context?.reference?.getBoundingClientRect()) {
      setWidth(context.reference.getBoundingClientRect().width);
    }
  }, [context?.reference]);

  if (!context) {
    throw new Error('Listbox must be used within a AvoSelectContext');
  }
  const { setFloating, x, y, strategy, isOpen } = context;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={(node) => {
            setFloating(node);
          }}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            width: width,
          }}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.1 }}
          {...(props as any)}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const AvoSelectItem = <T,>({ children, value, ...props }: AvoSelectItemProps<T>) => {
  const context = useContext(AvoSelectContext) as AvoSelectContextType<T> | undefined;
  if (!context) {
    throw new Error('Item must be used within a AvoSelectContext');
  }
  const { selectedItem, setSelectedItem, setIsOpen } = context;
  const isSelected = selectedItem === value;
  return (
    <div
      onClick={() => {
        setSelectedItem(value);
        setIsOpen(false);
      }}
      {...props}
    >
      {typeof children === 'function' ? children({ isSelected }) : children}
    </div>
  );
};
